import type { ConversationMessagesQuery } from "@qw/graphql";

import { adaptConversationMessagesToUIMessages } from "./chat.helpers";
import type { ExtendedUIMessage } from "./chat.types";

/**
 * Exhaustive state of the server-authoritative history source (brief 011 PR 5).
 *
 * Modeled as a discriminated union so the provider MUST handle every non-`ready`
 * outcome explicitly. A query failure or an `UnsupportedRuntime` capability result can
 * never be silently collapsed into a valid empty transcript — an empty conversation is
 * representable ONLY by `ready` with an empty `messages` array.
 */
export type ServerHistoryState =
  | { status: "loading" }
  | { status: "ready"; messages: ExtendedUIMessage[] }
  | { status: "unsupported"; reason: string }
  | { status: "error" };

/**
 * Pure mapping from the raw query outcome to the exhaustive render state. Kept separate
 * (and exported) so the failure / unsupported / ready / loading classification is
 * unit-testable without an Apollo harness.
 *
 * Last-good data wins: a `ConversationMessages` result stays `ready` even if a later
 * background refetch sets `hasError`, so a transient refetch failure never blanks an
 * already-rendered conversation. `error` is reserved for a cold read that produced no
 * usable result — the case that must fall back / surface, never render blank. Any other
 * no-result state (in flight, or skipped/pre-init) is `loading`, the safe non-blank base.
 */
export function deriveServerHistoryState(
  result: ConversationMessagesQuery["conversationMessages"] | undefined,
  hasError: boolean
): ServerHistoryState {
  if (result?.__typename === "ConversationMessages") {
    return { status: "ready", messages: adaptConversationMessagesToUIMessages(result.messages) };
  }
  if (result?.__typename === "UnsupportedRuntime") {
    return { status: "unsupported", reason: result.reason };
  }
  if (hasError) {
    return { status: "error" };
  }
  return { status: "loading" };
}
